/**
 * Top N par dimension. Quand `display.drill_dimensions` est renseigné, cliquer une
 * ligne descend d'un niveau (cf. `useDrillDown`) ; sans lui, les lignes sont inertes.
 */
import type { WidgetConfig } from '../schemas';
import { useFieldCatalog, metricLabel, metricFormat, dimensionLabel } from '../fieldCatalog';
import { formatMetricValue } from '../formatValue';
import { useDrillDown } from '../useDrillDown';
import { DrillBreadcrumb } from '../DrillBreadcrumb';
import { aggregateCategoryItems } from './categoryItems';

type Row = Record<string, string | number>;

interface WidgetProps {
  config: WidgetConfig;
  data: { current: Row[]; previous: Row[] | null; meta: any } | null;
  loading?: boolean;
  error?: string;
}

const DEFAULT_LIMIT = 10;

export default function TopListWidget({ config, data, loading, error }: WidgetProps) {
  // Hooks avant tout early return (règles des hooks) : libellés FR + format des valeurs.
  const catalog = useFieldCatalog(data?.meta?.source_type as string | undefined);

  const display = (config.display ?? {}) as Record<string, unknown>;
  const drillDimensions = (display.drill_dimensions as string[] | undefined) ?? [];
  const firstMetric = config.metrics?.[0];
  const firstDimension = config.dimensions?.[0];
  const period = (data?.meta as { period?: { start: string; end: string } } | undefined)?.period;

  const drill = useDrillDown({
    enabled:
      drillDimensions.length > 0 &&
      !!config.data_source_id &&
      !!firstMetric &&
      !!firstDimension &&
      !!period,
    dimension: firstDimension ?? '',
    drillDimensions,
    dataSourceId: config.data_source_id ?? '',
    metric: firstMetric ?? '',
    topN: (display.limit as number | undefined) ?? DEFAULT_LIMIT,
    period: period ?? { start: '', end: '' },
  });

  // Un échec de drill-down (400, timeout…) s'affiche dans l'état d'erreur du widget.
  const shownError = error ?? drill.error;

  if (loading) {
    return (
      <div className="h-full">
        <div className="h-4 w-1/3 bg-[#F4F5FA] rounded animate-pulse mb-4" />
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-4 w-full bg-[#F4F5FA] rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (shownError) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-sm text-red-600">{shownError}</span>
      </div>
    );
  }

  const metric = config.metrics?.[0];
  const dimension = config.dimensions?.[0];

  if (!data || !config.data_source_id || !metric || !dimension) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  const limit = (config.display?.limit as number | undefined) ?? DEFAULT_LIMIT;
  const format = metricFormat(catalog, metric);

  // Niveau descendu : ce sont les items du drill qu'on liste (déjà triés et tronqués).
  const isDrilled = drill.drillPath.length > 0 && !!drill.drillItems;

  // Même brique que donut/treemap/carte : regroupement par valeur de dimension (une
  // ligne par (jour, pays) sinon « France » apparaîtrait 30 fois), ratio recomposé,
  // buckets temporels formatés, tri décroissant et troncature.
  const items = (
    isDrilled
      ? (drill.drillItems ?? [])
      : aggregateCategoryItems(data.current, dimension, metric, catalog, limit)
  ).map((item) => ({ key: item.key, label: item.key, value: item.value }));

  const max = items.reduce((m, x) => Math.max(m, x.value), 1);
  const title = config.title || `${metricLabel(catalog, metric, config.metric_labels)} par ${dimensionLabel(catalog, dimension)}`;

  return (
    <div className="h-full overflow-auto">
      <div className="text-sm font-medium text-[#1B2559] mb-3">{title}</div>
      <DrillBreadcrumb drillPath={drill.drillPath} catalog={catalog} onDrillTo={drill.drillTo} />
      {drill.isLoading ? (
        <div className="text-sm text-[#8B8FA7]">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[#8B8FA7] italic">Aucune donnée</div>
      ) : (
        <ul className="space-y-0">
          {items.map((item) => (
            <li
              key={item.key}
              onClick={() => drill.canDrillDown && void drill.handleDrillDown(item.key)}
              className={
                drill.canDrillDown
                  ? 'flex items-center justify-between gap-4 px-1 py-1.5 cursor-pointer rounded hover:bg-[#F4F5FA]'
                  : 'flex items-center justify-between gap-4 px-1 py-1.5'
              }
            >
              <span className="truncate flex-1 text-sm text-[#1B2559]">{item.label}</span>
              <span className="text-sm tabular-nums text-[#1B2559]">{formatMetricValue(item.value, format)}</span>
              <div className="w-24 h-1 bg-[#F4F5FA] rounded">
                <div
                  className="h-1 bg-[#6366F1] rounded"
                  style={{ width: `${(item.value / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
